import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import { siteConfig } from '@/mocks/site';

export default function AboutPage() {
  const [email, setEmail] = useState('');
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotField = form.querySelector<HTMLInputElement>('input[name="company_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setSubmitStatus('success');
      setStatusMessage('感谢订阅！');
      return;
    }
    setSubmitStatus('loading');
    try {
      const formData = new FormData(form);
      const res = await fetch('https://readdy.ai/api/form/d9u52rv8l97n3eas3hi0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(text); } catch { /* noop */ }
      if (res.ok && (parsed as { code?: string }).code === 'OK') {
        setSubmitStatus('success');
        setStatusMessage('感谢订阅！我们会在有新文章时通知你。');
        setEmail('');
      } else {
        const msg = (parsed as { meta?: { message?: string } }).meta?.message
          || (parsed as { message?: string }).message
          || '订阅失败，请稍后重试';
        setSubmitStatus('error');
        setStatusMessage(msg);
      }
    } catch {
      setSubmitStatus('error');
      setStatusMessage('网络错误，请检查连接后重试');
    }
  };

  return (
    <>
      <SEO
        title="关于我们 — 悦己新知 | 科学认识身体，悦纳真实自我"
        description="悦己新知是专注于女性性健康科普的知识平台。了解我们的品牌故事、专业团队和使命愿景。联系邮箱：hello@yuejixinzhi.com"
        keywords="关于悦己新知,女性性健康科普,自慰喷水指南,潮喷科普,性教育平台"
        canonicalUrl="/about"
        pageType="AboutPage"
      />
      <Navbar />

      <main className="pt-16">
        <section className="bg-background-100 py-14 md:py-20">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <nav className="flex items-center justify-center gap-2 text-xs text-foreground-500 mb-6" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700">关于我们</span>
            </nav>
            <h1 className="font-heading text-3xl md:text-4xl font-semibold text-foreground-900 mb-4">
              关于 {siteConfig.name}
            </h1>
            <p className="text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              {siteConfig.description}
            </p>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="font-heading text-2xl font-semibold text-foreground-900 mb-4">我们的使命</h2>
                <div className="space-y-3 text-sm text-foreground-600 leading-relaxed">
                  <p>
                    在中国，性教育——尤其是面向女性的性教育——长期处于缺失状态。许多女性对自己的身体缺乏基本了解，对自慰、潮喷等正常的生理现象充满误解和羞耻感。
                  </p>
                  <p>
                    <strong className="text-foreground-800">悦己新知</strong>的诞生正是为了填补这一空白。我们相信，每个女性都有权利以科学、坦然的态度了解自己的身体。性健康是整体健康不可分割的一部分，而知识是消除恐惧和偏见最有力的工具。
                  </p>
                  <p>
                    我们的内容由医学、心理学和健康教育领域的专业人士撰写和审核，确保每一篇文章都基于科学证据，同时以温暖、易懂的语言呈现，让每个女性都能轻松获取可靠的知识。
                  </p>
                </div>
              </div>
              <div className="rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Abstract%20warm%20flowing%20organic%20shapes%20with%20gentle%20coral%20and%20cream%20tones%2C%20representing%20growth%20and%20knowledge%2C%20soft%20feminine%20energy%2C%20minimalist%20elegant%20art%20style%2C%20clean%20background%2C%20calm%20and%20empowering%20atmosphere&width=700&height=500&seq=about-mission&orientation=landscape"
                  alt="悦己新知使命"
                  className="w-full h-72 object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl font-semibold text-foreground-900 text-center mb-10">专业信誉</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { value: siteConfig.articleCount, label: '科普文章', icon: 'ri-article-line' },
                { value: siteConfig.authorCount, label: '专业作者', icon: 'ri-team-line' },
                { value: siteConfig.readerCount, label: '累计读者', icon: 'ri-user-heart-line' },
                { value: `自${siteConfig.foundingYear}年`, label: '持续更新', icon: 'ri-calendar-check-line' },
              ].map((stat) => (
                <div key={stat.label} className="text-center p-5 rounded-xl bg-background-50">
                  <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-600 mb-3">
                    <i className={`${stat.icon} text-lg`}></i>
                  </div>
                  <div className="font-heading text-2xl font-semibold text-foreground-900">{stat.value}</div>
                  <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <div>
                <h2 className="font-heading text-2xl font-semibold text-foreground-900 mb-4">我们的价值观</h2>
                <div className="space-y-4">
                  {[
                    { title: '科学为本', desc: '所有内容基于循证医学和权威研究，拒绝伪科学和虚假信息。' },
                    { title: '温暖包容', desc: '以去羞耻化、非评判的态度传递知识，让每个女性感到被理解和尊重。' },
                    { title: '隐私第一', desc: '我们深知性健康话题的敏感性，不追踪、不收集读者的个人阅读行为。' },
                    { title: '持续学习', desc: '性健康研究不断进展，我们持续更新内容，确保信息的时效性和准确性。' },
                  ].map((v) => (
                    <div key={v.title} className="flex gap-3">
                      <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 mt-0.5">
                        <i className="ri-check-line text-sm"></i>
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground-800">{v.title}</h3>
                        <p className="text-sm text-foreground-600 mt-0.5 leading-relaxed">{v.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h2 className="font-heading text-2xl font-semibold text-foreground-900 mb-4">联系我们</h2>
                <div className="bg-background-100 rounded-xl p-6">
                  <p className="text-sm text-foreground-600 mb-6 leading-relaxed">
                    如果你有任何问题、建议或合作意向，欢迎通过以下方式联系我们。我们珍视每一位读者的声音。
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                        <i className="ri-mail-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">电子邮箱</p>
                        <a href={`mailto:${siteConfig.email}`} className="text-sm text-foreground-800 hover:text-primary-600 transition-colors">
                          {siteConfig.email}
                        </a>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                        <i className="ri-global-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-xs text-foreground-500">官方网站</p>
                        <span className="text-sm text-foreground-800">{siteConfig.url}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-8">
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-4">订阅我们的通讯</h3>
                  <p className="text-sm text-foreground-600 mb-4 leading-relaxed">
                    第一时间获取最新科普文章，我们不会发送垃圾邮件，你可以随时取消订阅。
                  </p>
                  <form data-readdy-form onSubmit={handleSubscribe} className="flex flex-col gap-2">
                    <input
                      type="text"
                      name="company_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="visually-hidden-field"
                    />
                    <div className="flex gap-2">
                      <input
                        type="email"
                        name="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="输入你的邮箱"
                        required
                        className="flex-1 px-4 py-2.5 text-sm rounded-lg bg-background-100 border border-background-300 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                      />
                      <button
                        type="submit"
                        disabled={submitStatus === 'loading'}
                        className="px-5 py-2.5 text-sm font-medium rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap disabled:opacity-60 cursor-pointer"
                      >
                        {submitStatus === 'loading' ? '提交中...' : '订阅'}
                      </button>
                    </div>
                    {submitStatus !== 'idle' && (
                      <p className={`text-xs ${submitStatus === 'success' ? 'text-secondary-600' : 'text-red-500'}`}>
                        {statusMessage}
                      </p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}